import React, { useState, useEffect, useCallback } from 'react';
import '../Css/RouteOptimization.css';
import '@tabler/icons-webfont/dist/tabler-icons.min.css';
import MapNav from './MapNav';
import { useSearchParams, Navigate } from 'react-router-dom';

const STATUS_MAP = { '1': 'Assigned', '4': 'Picked Up', '5': 'On the Way' };
const STATUS_CLASS = { '1': 'status--assigned', '4': 'status--picked', '5': 'status--onway' };

const RouteOptimization = () => {
    const role = sessionStorage.getItem('role');
    const adminEmail = sessionStorage.getItem('userEmail') || '';
    const [searchParams] = useSearchParams();
    const preselectDriverEmail = searchParams.get('driver') || '';
    const [drivers, setDrivers] = useState([]);
    const [loadingDrivers, setLoadingDrivers] = useState(true);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [optimizing, setOptimizing] = useState(false);
    const [message, setMessage] = useState('');
    const [messageKind, setMessageKind] = useState('ok');
    const [activeCountMap, setActiveCountMap] = useState({});

    const showMsg = (text, kind = 'ok') => {
        setMessage(text);
        setMessageKind(kind);
    };

    const loadDrivers = useCallback(async () => {
        if (!adminEmail) return;
        setLoadingDrivers(true);
        try {
            const res = await fetch(`/api/dispatches/${adminEmail}`);
            if (!res.ok) throw new Error('Failed to load drivers');
            const data = await res.json();
            const list = Array.isArray(data) ? data : [];
            setDrivers(list);

            const counts = {};
            await Promise.all(
                list.map(async (d) => {
                    try {
                        const r = await fetch(`/api/DrOrder/order/${d.email}`);
                        if (!r.ok) return;
                        const ordersData = await r.json();
                        const active = Array.isArray(ordersData)
                            ? ordersData.filter((o) => o.o_status !== '2' && o.o_status !== '3').length
                            : 0;
                        counts[d.email] = active;
                    } catch {
                        counts[d.email] = 0;
                    }
                })
            );
            setActiveCountMap(counts);
        } catch (e) {
            showMsg(e.message || 'Could not load drivers', 'bad');
            setDrivers([]);
        } finally {
            setLoadingDrivers(false);
        }
    }, [adminEmail]);

    const loadOrders = useCallback(async (driver) => {
        if (!driver?.email) return;
        setLoadingOrders(true);
        setMessage('');
        try {
            const res = await fetch(`/api/DrOrder/order/${driver.email}`);
            if (!res.ok) throw new Error('Failed to load orders');
            const data = await res.json();
            const active = Array.isArray(data)
                ? data.filter((o) => o.o_status !== '2' && o.o_status !== '3')
                : [];
            active.sort((a, b) => {
                const sa = a.stop_sequence ?? 9999;
                const sb = b.stop_sequence ?? 9999;
                if (sa !== sb) return sa - sb;
                return String(a.Order_No || '').localeCompare(String(b.Order_No || ''));
            });
            setOrders(active);
        } catch (e) {
            showMsg(e.message || 'Could not load orders', 'bad');
            setOrders([]);
        } finally {
            setLoadingOrders(false);
        }
    }, []);

    useEffect(() => {
        loadDrivers();
    }, [loadDrivers]);

    useEffect(() => {
        if (!preselectDriverEmail || !drivers.length) return;
        const match = drivers.find((d) => d.email === preselectDriverEmail);
        if (match) {
            setSelectedDriver(match);
            loadOrders(match);
        }
    }, [drivers, preselectDriverEmail, loadOrders]);

    const selectDriver = (driver) => {
        setSelectedDriver(driver);
        loadOrders(driver);
    };

    const handleOptimize = async () => {
        if (!selectedDriver?.email || !adminEmail) return;
        if (orders.length < 2) {
            showMsg('At least 2 active orders are needed to optimize a route', 'warn');
            return;
        }
        setOptimizing(true);
        setMessage('');
        try {
            const res = await fetch(`/api/routes/optimize?adminEmail=${encodeURIComponent(adminEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    driverEmail: selectedDriver.email,
                    apply: true,
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || data.message || 'Optimization failed');
            }
            showMsg(
                `Route optimized — ${data.stops?.length || 0} stops · ${data.totalKm} km · ~${data.totalMinutes} min` +
                (data.skipped?.length ? ` (${data.skipped.length} skipped — missing coordinates)` : ''),
                'ok'
            );
            await loadOrders(selectedDriver);
            await loadDrivers();
        } catch (e) {
            showMsg(e.message || 'Could not optimize route', 'bad');
        } finally {
            setOptimizing(false);
        }
    };

    const optimizableCount = drivers.filter((d) => (activeCountMap[d.email] || 0) >= 2).length;

    if (role === 'dispatcher' || role === 'packer') {
        return <Navigate to="/Map" replace />;
    }

    return (
        <div className="ro-page">
            <MapNav />

            <div className="ro-header">
                <div>
                    {/* <h1 className="ro-title"><i className="ti ti-route"></i> Route Optimization</h1>
                    <p className="ro-subtitle">
                        Plan the best delivery sequence for each driver. Uses geocoded addresses — no extra API cost.
                    </p> */}
                    <h1 className="ro-title"><i className="ti ti-route"></i> Plan the best delivery sequence for each driver.</h1>
                </div>
            </div>

            <div className="ro-kpis">
                <div className="ro-kpi">
                    <div className="ro-kpi-label">Drivers</div>
                    <div className="ro-kpi-val">{loadingDrivers ? '…' : drivers.length}</div>
                </div>
                <div className="ro-kpi">
                    <div className="ro-kpi-label">Ready to optimize</div>
                    <div className="ro-kpi-val">{loadingDrivers ? '…' : optimizableCount}</div>
                    <div className="ro-kpi-foot">2+ active orders</div>
                </div>
                <div className="ro-kpi">
                    <div className="ro-kpi-label">Selected driver</div>
                    <div className="ro-kpi-val ro-kpi-val-sm">{selectedDriver?.name || '—'}</div>
                </div>
            </div>

            <div className="ro-layout">
                <div className="ro-panel">
                    <div className="ro-panel-head">
                        <span className="ro-panel-title">Drivers</span>
                        <button type="button" className="ro-icon-btn" onClick={loadDrivers} title="Refresh">
                            <i className="ti ti-refresh"></i>
                        </button>
                    </div>
                    <div className="ro-panel-body ro-driver-list">
                        {loadingDrivers ? (
                            <div className="ro-empty">Loading drivers…</div>
                        ) : drivers.length === 0 ? (
                            <div className="ro-empty">No drivers found</div>
                        ) : (
                            drivers.map((d) => {
                                const count = activeCountMap[d.email] ?? '…';
                                const selected = selectedDriver?.email === d.email;
                                return (
                                    <button
                                        key={d.email}
                                        type="button"
                                        className={`ro-driver-row ${selected ? 'selected' : ''}`}
                                        onClick={() => selectDriver(d)}
                                    >
                                        <div className="ro-driver-name">{d.name}</div>
                                        <div className="ro-driver-meta">
                                            {d.area || 'No area'} · {count} active
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="ro-panel ro-panel-main">
                    <div className="ro-panel-head ro-panel-head-split">
                        <div>
                            <span className="ro-panel-title">
                                {selectedDriver ? `${selectedDriver.name}'s route` : 'Select a driver'}
                            </span>
                            {selectedDriver && (
                                <span className="ro-panel-sub">
                                    {loadingOrders ? 'Loading…' : `${orders.length} active order${orders.length !== 1 ? 's' : ''}`}
                                </span>
                            )}
                        </div>
                        {selectedDriver && orders.length >= 2 && (
                            <button
                                type="button"
                                className="ro-primary-btn"
                                disabled={optimizing || loadingOrders}
                                onClick={handleOptimize}
                            >
                                <i className="ti ti-route"></i>
                                {optimizing ? 'Optimizing…' : 'Optimize route'}
                            </button>
                        )}
                    </div>

                    {message && (
                        <div className={`ro-banner ro-banner--${messageKind}`}>{message}</div>
                    )}

                    <div className="ro-panel-body">
                        {!selectedDriver ? (
                            <div className="ro-empty ro-empty-lg">
                                <i className="ti ti-click"></i>
                                <p>Select a driver from the list to view orders and optimize their delivery route.</p>
                            </div>
                        ) : loadingOrders ? (
                            <div className="ro-empty">Loading orders…</div>
                        ) : orders.length === 0 ? (
                            <div className="ro-empty">No active orders for this driver</div>
                        ) : orders.length === 1 ? (
                            <div className="ro-empty">Only 1 active order — assign more before optimizing</div>
                        ) : (
                            orders.map((order, idx) => (
                                <div key={order._id || idx} className="ro-order-card">
                                    <div className="ro-order-top">
                                        <strong>
                                            {order.stop_sequence ? `#${order.stop_sequence} · ` : ''}{order.Order_No}
                                        </strong>
                                        <span className={`ro-status ${STATUS_CLASS[order.o_status] || ''}`}>
                                            {STATUS_MAP[order.o_status] || 'In progress'}
                                        </span>
                                    </div>
                                    <div className="ro-order-meta">₹{order.amount} · {order.Rec_Name || '—'}</div>
                                    <div className="ro-order-addr">{order.Rec_Add || '—'}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RouteOptimization;
